import { internalData } from '../../store/store.ts';
import {
  registerParamateInput,
  unregisterParamateInput,
  markParamPending,
  clearParamPending,
  type ParamateLinkedInput,
} from '../../store/paramInputRegistry.ts';
import { isProduction } from '../../data/constants.ts';
import { currentProjectData } from '../../data/projectManagement.ts';
import './as-param-warning.ts';
import { HistoryTrackingMixin } from './history-tracking-mixin.ts';
import { AsEl, applyLabelVars } from '../base.ts';
import type { InputChangeReason } from './types.ts';
import { sendEv } from '../../data/events.ts';
import { lookupParam } from '../../store/paramStore.ts';
import { withConfigurator } from '../../util/singletons.ts';

export type InternalSetValueOptions = {
  emitInput?: boolean;
  /** true fires `as-input-change`, false fires `as-input-sync` instead. */
  emitChange?: boolean;
  reflectAttr?: boolean;
  commit?: boolean;
  /** Emit nothing at all — for first-render paths that only establish state. */
  silent?: boolean;
  /** Defaults to 'api'. */
  reason?: InputChangeReason;
};

export type SyncMode = 'auto' | 'manual';

export type TrCadInputType = undefined|'string'|'int'|'float'|'bool'|'color'|'matrix'|'vector2d'|'vector3d'|'vector'|'matrix'|'curve'|'sketch'

// This interface acts as your "source of truth" for type mapping
export interface TrCadTypeMap {
  undefined: unknown;
  'string': string;
  'int': number;
  'float': number;
  'bool': boolean;
  'color': string;
  'matrix': number[];
  'vector2d': [number, number];
  'vector3d': [number, number, number];
  'vector': number[];
  'curve': any; // Define specific types as needed
  'sketch': any;
}
/**
 * Base class for input components with shared Paramate integration.
 * Works with both Light DOM and Shadow DOM implementations.
 *
 * Attributes:
 * - trcad-param: Parameter name for Paramate integration
 * - sync-mode: 'auto' (default) or 'manual'
 *   - auto: Syncs with Paramate automatically on value changes
 *   - manual: Requires explicit sync via as-param-needs-sync event (TODO)
 * - debounce-ms: Debounce delay in ms for Paramate communication (default: 150)
 * - no-debounce: Disables debounce entirely (sets delay to 0)
 *
 * Subclasses must implement:
 * - _getValue(): returns the current value
 * - _applyValue(val, options): sets the value internally
 * - _convertFromParamate(val): converts Paramate value to component's type
 * - _getInputElement(): returns the input element (for disabled state)
 */
export abstract class AsInputBase<T extends TrCadInputType> extends HistoryTrackingMixin(AsEl) {
  static readonly DEFAULT_DEBOUNCE_MS = 150;

  public trCadType: T


  protected _value: TrCadTypeMap[T];
  get value(){
    return this._value
  }
  set value(value:any){
      if(this._checkType(value)){
        this._value = value
      }
  }

  // Paramate integration
  protected _paramName: string = '';
  protected _paramateRegistryEntry: ParamateLinkedInput | null = null;
  protected _lastConfirmedValue: any = null;
  protected _configurator: any = null;
  protected _syncMode: SyncMode = 'auto';

  // The value this input goes back to on a reset (New Session, resetToDefault).
  // Captured once, on the first connect, and never re-captured — see
  // _captureDefault below for why that matters.
  protected _defaultValue: TrCadTypeMap[T] | undefined;
  private _defaultCaptured = false;

  protected _isParamMissing: boolean = false;
  public get isParamMissing(): boolean {
    return this._isParamMissing && !isProduction;
  }

  // Placeholder values for {0}, {1}, ... in the label — set programmatically,
  // no attribute equivalent (labels are markup, vars are runtime data).
  // Subclasses that render a label as-text override _onLabelVarsChanged to
  // push _labelVars onto it from their own label-update method.
  protected _labelVars: any[] | null = null;
  get labelVars(): any[] | null { return this._labelVars; }
  set labelVars(value: any[] | null) {
    this._labelVars = value;
    this._onLabelVarsChanged();
  }
  protected _onLabelVarsChanged(): void { /* no-op by default */ }

  /** Set/clear the vars attribute on a label as-text before applyLocalizedText re-renders it. */
  protected _applyLabelVars(labelEl: HTMLElement): void {
    applyLabelVars(labelEl, this._labelVars);
  }

  // Configure-on-change: when true, setParamValue triggers immediate configure()
  protected _configureOnChange: boolean = false;

  // Ignore-history: when true, value changes are not pushed to the undo stack
  protected _ignoreHistory: boolean = false;

  // Debounce
  protected _debounceMs: number = AsInputBase.DEFAULT_DEBOUNCE_MS;
  private _debounceTimer: ReturnType<typeof setTimeout> | null = null;

  // Base-class attribute observer (trcad-param, sync-mode, debounce-ms, no-debounce, cooloff)
  private _baseAttrObserver: MutationObserver | null = null;
  private _boundRestoreInputValues: ((event: Event) => void) | null = null;
  private _boundRestoreParamValues: ((event: Event) => void) | null = null;
  private _boundProjectNew: ((event: Event) => void) | null = null;
  private _boundAppDataChanged: ((event: Event) => void) | null = null;
  private _appDataValueUnbinds: Array<() => void> = [];
  /** appData keys this input is two-way bound to, newest last. */
  private _boundAppDataKeys: string[] = [];

  // Cooloff/throttle for event emission
  protected _cooloffMs: number = 0;
  private _cooloffInputState = { inCooloff: false, timer: null as ReturnType<typeof setTimeout> | null, queuedFn: null as (() => void) | null };
  private _cooloffChangeState = { inCooloff: false, timer: null as ReturnType<typeof setTimeout> | null, queuedFn: null as (() => void) | null };

  public isArrayType = false


  // ============================================================================
  // Abstract methods - subclasses must implement
  // ============================================================================

  /** Get the current value */
  protected abstract _getValue(): TrCadTypeMap[T];

  _getHistoryValue(): any { return this._getValue(); }

  /** A bound input's edit is recorded by the appData write, or by nothing when
   *  the binding is silent. An input entry on top would be a second record of
   *  the same click. */
  _historyOwnedElsewhere(): boolean { return this._boundAppDataKeys.length > 0; }

  /** Apply value internally with options */
  protected abstract _applyValue(
      val: TrCadTypeMap[T], 
      options: InternalSetValueOptions
    ): void;


  


  /** Convert Paramate value to component's native type (e.g., Boolean, String, Number) */
  protected abstract _convertFromParamate(val: any): any;

  /** Get the input element for disabled state handling */
  protected abstract _getInputElement(): HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement | null;

  /**
   * Hook called when appData changes. Subclasses can override this to
   * react to application-level state changes.
   */
  protected _onAppDataUpdate(_appData: Record<string, any>): void { /* optional hook */ }

  //TODO:@REMOVE
  /** @deprecated use `onAppDataUpdate` instead. Called alongside it (default
   *  no-op implementation forwards there is not needed since both fire from
   *  `_handleAppDataChanged`). */
  protected _onCustomDataUpdate(_customData: Record<string, any>): void { /* optional hook */ }

  /**
   * Two-way binds this input's value to an appData key — the AsInputBase
   * counterpart to AsEl.syncWithProjData (which is DOM-attr/method only and
   * one-directional). Applies the current value, and every later external
   * change, via `_applyValue` (silently — no re-emitted input/change event,
   * so this can't loop); writes the input's own value back into appData
   * whenever the user changes it (the 'as-input-change' event every
   * AsInputBase subclass dispatches — not the native 'change', which numbox
   * and AsCheckbox don't fire).
   *
   * Call from connectedCallback(), after rendering (or from a non-subclass
   * consumer, e.g. a Mithril `oncreate` hook — it's public precisely so an
   * app that only ever creates these elements, never subclasses them, can
   * still use it).
   *
   * For a side effect on the value changing — kicking off a recompute, pushing
   * it at the CAD script — subscribe to the key rather than hooking the write:
   *
   *   this.bindWithAppData('internalBorderWidth');
   *   app.appData.subscribeKey('internalBorderWidth', (v) => vectorize(v));
   *
   * That covers a project load, an undo and a programmatic write as well as a
   * user edit, which a hook on this element's own change event cannot.
   *
   * A bound input pushes no input entry of its own. The appData write is the
   * undo record, or with `silent` there is none, as asked.
   *
   * Cleanup is automatic (via `_unregisterParamate()`, called from every
   * subclass's own `disconnectedCallback`) — the returned unbind function is
   * only for stopping early, same as `syncWithProjData`.
   */
  public bindWithAppData(
    key: string,
    opts?: { silent?: boolean },
  ): () => void {
    // The cast is needed because `this` inside ProjDataSyncTargets<this> doesn't
    // propagate through the HistoryTrackingMixin(AsEl) mixin chain when called as
    // `this.syncWithProjData(...)` from within this class's own method (a known TS
    // limitation with polymorphic `this` across generic mixin layers — confirmed
    // AsEl.syncWithProjData DOES infer correctly for a plain `el.syncWithProjData(...)`
    // call). `_applyAppDataValue` below is a real member; this doesn't hide a typo
    // the way it normally would be caught.
    const unbindRead = this.syncWithProjData(key, (value) => ({
      methods: { _applyAppDataValue: value } as Partial<Record<keyof this & string, any>>,
    }));
    this._boundAppDataKeys.push(key);
    const onChange = () => {
      const value = this._getValue();
      // patchAppData (unlike setAppData) merges rather than replaces,
      // so unrelated seeds aren't wiped by a single-key write.
      currentProjectData.patchAppData({ [key]: value }, opts?.silent);
    };
    // 'as-input-change' — not the native 'change' — is the one event every
    // AsInputBase subclass actually dispatches on itself (numbox and
    // AsCheckbox never fire a plain 'change'; as-string-input/as-dropdown do,
    // but only as an extra legacy convenience).
    this.addEventListener('as-input-change', onChange);
    const unbind = () => {
      unbindRead();
      this.removeEventListener('as-input-change', onChange);
      this._appDataValueUnbinds = this._appDataValueUnbinds.filter(u => u !== unbind);
      const at = this._boundAppDataKeys.lastIndexOf(key);
      if (at !== -1) this._boundAppDataKeys.splice(at, 1);
    };
    // Auto-cleaned by _unregisterParamate() (called from every subclass's own
    // disconnectedCallback) — same guarantee as syncWithProjData's own
    // auto-cleanup, so callers (e.g. a Mithril oncreate hook with no matching
    // onremove) aren't required to call the returned unbind themselves.
    this._appDataValueUnbinds.push(unbind);
    return unbind;
  }

  //TODO:@REMOVE
  /** @deprecated use `bindWithAppData` instead. */
  public bindWithCustomData(
    key: string,
    opts?: { silent?: boolean },
  ): () => void {
    return this.bindWithAppData(key, opts);
  }

  /** bindWithAppData's read-side setter target — applies an externally
   *  changed appData value without re-emitting input/change (see above). */
  private _applyAppDataValue(value: TrCadTypeMap[T]): void {
    if (value === undefined) return;
    if (this._areValuesEqual(value, this._getValue())) return;
    this._applyValue(value, { emitInput: false, emitChange: false, reflectAttr: true, commit: true, reason: 'app-data' });
  }

  /** The value a reset goes back to. Set it to declare a default from script,
   *  the same as the `default-value` attribute does from markup. */
  get defaultValue(): TrCadTypeMap[T] | undefined {
    return this._defaultValue;
  }
  set defaultValue(val: TrCadTypeMap[T] | undefined) {
    this._defaultValue = val;
    this._defaultCaptured = true;
  }

  /**
   * Where the default comes from when it isn't declared outright, in order:
   * the authored `value` attribute, else whatever the value happens to be at
   * first connect.
   *
   * Overridable because not every input keeps its value in a `value`
   * attribute — as-checkbox uses `checked`.
   */
  protected _authoredDefault(): TrCadTypeMap[T] | undefined {
    const attr = this.getAttribute('value');
    return attr === null ? undefined : this._convertFromParamate(attr);
  }

  /**
   * Runs once, on the first connect only. Re-capturing would be wrong: most
   * inputs reflect their live value back onto the `value`/`checked` attribute,
   * so an element that is disconnected and reconnected after the user edits it
   * (a step panel re-parenting, a wrapper re-render) would quietly adopt the
   * edited value as its new default.
   */
  private _captureDefault(): void {
    if (this._defaultCaptured) return;
    this._defaultCaptured = true;

    const declared = this.getAttribute('default-value');
    if (declared !== null) {
      this._defaultValue = this._convertFromParamate(declared);
      return;
    }

    const authored = this._authoredDefault();
    // No attribute to read means the value came from a property, a binding or a
    // param. Snapshot it anyway — otherwise the input has no default at all and
    // a reset silently does nothing, which is the old behaviour we're fixing.
    this._defaultValue = authored !== undefined ? authored : this._getValue();
  }

  /**
   * What a reset actually goes back to.
   *
   * A bound input defers to its appData key. On New Session the store is
   * reverted to the app's registered seeds *before* `as-inputs-reset` fires, so
   * the seed is already sitting there — taking it keeps the input and appData
   * agreeing instead of the input quietly showing its own markup default while
   * the store says something else. Unbound inputs use the captured default.
   */
  private _resetTarget(): TrCadTypeMap[T] | undefined {
    for (let i = this._boundAppDataKeys.length - 1; i >= 0; i--) {
      const seeded = (currentProjectData.appData as any)?.[this._boundAppDataKeys[i]];
      if (seeded !== undefined) return seeded as TrCadTypeMap[T];
    }
    return this._defaultValue;
  }

  /**
   * Reset the input to its default value. Subclasses shouldn't need to override
   * this — the default is already converted to the component's own type.
   */
  public resetToDefault(opt?: { silenceChange?: boolean }): void {
    const silenceChange = opt?.silenceChange ?? true; // Default to silent
    const target = this._resetTarget();
    if (target === undefined) return;
    this._applyValue(target, {
      emitInput: false,
      emitChange: !silenceChange,
      reflectAttr: true,
      commit: true,
      reason: 'inputs-reset',
    });
    this._lastConfirmedValue = this._getValue();
  }

  /**
   * Project reset. `ignore-reset` marks chrome that sits outside the project,
   * like the header language picker or the export settings. A new project
   * leaves it alone, and it is never saved or put on the undo stack.
   */
  private _handleProjectNew = (event: Event): void => {
    if (this.hasAttribute('ignore-reset')) return;
    const silenceChange = (event as CustomEvent)?.detail?.silenceChange ?? true;
    this.resetToDefault({ silenceChange });
  };

  // ============================================================================
  // Paramate Registration (call from subclass lifecycle methods)
  // ============================================================================

  /** Call from connectedCallback after rendering */
  protected _registerParamate(): void {
    // Capture the reset target before anything (a param sync, a binding) can
    // move the value. First connect only.
    this._captureDefault();

    // Read initial attribute values
    this._syncMode = this.getAttribute('sync-mode') === 'manual' ? 'manual' : 'auto';
    this._configureOnChange = this.hasAttribute('configure-on-change');
    this._ignoreHistory = this.hasAttribute('ignore-history');
    this._readDebounceAttributes();
    this._readCooloffAttribute();

    if (this.hasAttribute('trcad-param')) {
      this._paramName = this.getAttribute('trcad-param') ?? '';
      if (this._paramName) {
        this._paramateRegistryEntry = registerParamateInput(this._paramName, this);
        this._lastConfirmedValue = this._getValue();
        this._requestConfigurator();
      }
    }

    // Single observer for all base-class attributes
    this._baseAttrObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        switch (m.attributeName) {
          case 'trcad-param':
            this._onParamAttributeChange(this.getAttribute('trcad-param'));
            break;
          case 'sync-mode':
            this._syncMode = this.getAttribute('sync-mode') === 'manual' ? 'manual' : 'auto';
            break;
          case 'configure-on-change':
            this._configureOnChange = this.hasAttribute('configure-on-change');
            break;
          case 'ignore-history':
            this._ignoreHistory = this.hasAttribute('ignore-history');
            break;
          case 'debounce-ms':
          case 'no-debounce':
            this._readDebounceAttributes();
            break;
          case 'cooloff':
            this._readCooloffAttribute();
            break;
          case 'default-value': {
            // An explicit re-declaration always wins, even after capture.
            const declared = this.getAttribute('default-value');
            this._defaultValue = declared === null
              ? undefined
              : this._convertFromParamate(declared);
            break;
          }
          case 'readonly':
          case 'locked':
            this._applyReadonlyState();
            break;
        }
      }
    });
    this._baseAttrObserver.observe(this, {
      attributes: true,
      attributeFilter: ['trcad-param', 'sync-mode', 'configure-on-change', 'ignore-history', 'debounce-ms', 'no-debounce', 'cooloff', 'readonly', 'locked', 'default-value'],
    });

    this.addEventListener('as-paramate-value-resolved', this._handleParamateValueResolved as EventListener);

    // Listen for project restore — self-apply saved value by element ID
    this._boundRestoreInputValues = this._handleRestoreInputValues.bind(this);
    window.addEventListener('as-restore-input-values', this._boundRestoreInputValues);

    // Listen for param-keyed undo/redo restore (linked inputs only)
    this._boundRestoreParamValues = this._handleRestoreParamValues.bind(this);
    window.addEventListener('as-restore-param-values', this._boundRestoreParamValues);

    // Listen for project reset.
    this._boundProjectNew = this._handleProjectNew.bind(this) as any;
    window.addEventListener('as-inputs-reset', this._boundProjectNew);

    // Listen for appData changes. Only one listener is needed even during the
    // customData -> appData event-name transition, since projectManagement.ts
    // fires as-app-data-changed and as-custom-data-changed together, back to back.
    this._boundAppDataChanged = this._handleAppDataChanged.bind(this);
    window.addEventListener('as-app-data-changed', this._boundAppDataChanged);

    // Apply initial readonly state
    this._applyReadonlyState();

    this._registerHistoryTracking();
  }

  /** Call from disconnectedCallback */
  protected _unregisterParamate(): void {
    // Clean up debounce
    if (this._debounceTimer !== null) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = null;
    }
    // Clean up cooloff timers
    if (this._cooloffInputState.timer !== null) {
      clearTimeout(this._cooloffInputState.timer);
    }
    if (this._cooloffChangeState.timer !== null) {
      clearTimeout(this._cooloffChangeState.timer);
    }
    this._cooloffInputState = { inCooloff: false, timer: null, queuedFn: null };
    this._cooloffChangeState = { inCooloff: false, timer: null, queuedFn: null };

    if (this._baseAttrObserver) {
      this._baseAttrObserver.disconnect();
      this._baseAttrObserver = null;
    }

    if (this._paramName) {
      unregisterParamateInput(this._paramName, this);
      this._paramateRegistryEntry = null;
    }

    this.removeEventListener('as-paramate-value-resolved', this._handleParamateValueResolved as EventListener);

    if (this._boundRestoreInputValues) {
      window.removeEventListener('as-restore-input-values', this._boundRestoreInputValues);
      this._boundRestoreInputValues = null;
    }

    if (this._boundRestoreParamValues) {
      window.removeEventListener('as-restore-param-values', this._boundRestoreParamValues);
      this._boundRestoreParamValues = null;
    }

    if (this._boundProjectNew) {
      window.removeEventListener('as-inputs-reset', this._boundProjectNew);
      this._boundProjectNew = null;
    }

    if (this._boundAppDataChanged) {
      window.removeEventListener('as-app-data-changed', this._boundAppDataChanged);
      this._boundAppDataChanged = null;
    }

    this._appDataValueUnbinds.forEach(unbind => unbind());
    this._appDataValueUnbinds = [];
    this._boundAppDataKeys = [];

    this._unregisterHistoryTracking();
  }

  private _handleAppDataChanged(event: Event): void {
    const appData = (event as CustomEvent).detail?.appData ?? currentProjectData.appData;
    this._onAppDataUpdate(appData);
    this._onCustomDataUpdate(appData);
  }

  private _onParamAttributeChange(newValue: string | null): void {
    // Unregister old param if exists
    if (this._paramName) {
      unregisterParamateInput(this._paramName, this);
      this._paramateRegistryEntry = null;
    }

    this._paramName = (newValue ?? '').trim();

    // Register new param if set
    if (this._paramName) {
      this._paramateRegistryEntry = registerParamateInput(this._paramName, this);
      this._lastConfirmedValue = this._getValue();
      this._requestConfigurator();
    }
  }

  /** Reads debounce-ms and no-debounce attributes and updates _debounceMs */
  private _readDebounceAttributes(): void {
    if (this.hasAttribute('no-debounce')) {
      this._debounceMs = 0;
      return;
    }
    const debounceMsAttr = this.getAttribute('debounce-ms');
    if (debounceMsAttr !== null) {
      const parsed = parseInt(debounceMsAttr, 10);
      this._debounceMs = (Number.isFinite(parsed) && parsed >= 0) ? parsed : AsInputBase.DEFAULT_DEBOUNCE_MS;
    } else {
      this._debounceMs = AsInputBase.DEFAULT_DEBOUNCE_MS;
    }
  }

  // ============================================================================
  // Cooloff / Throttle for Event Emission
  // ============================================================================

  /** Read the cooloff attribute and update _cooloffMs */
  private _readCooloffAttribute(): void {
    const cooloffAttr = this.getAttribute('cooloff');
    if (cooloffAttr !== null) {
      const parsed = parseInt(cooloffAttr, 10);
      this._cooloffMs = (Number.isFinite(parsed) && parsed >= 0) ? parsed : 0;
    } else {
      this._cooloffMs = 0;
    }
  }

  /**
   * Throttle event emission by channel ('input' or 'change').
   * If not in cooloff, fn() runs immediately and cooloff starts.
   * If in cooloff, fn is queued (latest wins) and runs when cooloff expires.
   * When cooloffMs is 0, fn() runs immediately with no throttling.
   */
  protected _throttle(channel: 'input' | 'change', fn: () => void): void {
    if (this._cooloffMs <= 0) {
      fn();
      return;
    }
    const state = channel === 'input' ? this._cooloffInputState : this._cooloffChangeState;
    if (!state.inCooloff) {
      fn();
      this._startCooloff(channel);
    } else {
      state.queuedFn = fn;
    }
  }

  private _startCooloff(channel: 'input' | 'change'): void {
    if (this._cooloffMs <= 0) return;
    const state = channel === 'input' ? this._cooloffInputState : this._cooloffChangeState;
    state.inCooloff = true;
    if (state.timer !== null) clearTimeout(state.timer);
    state.timer = setTimeout(() => {
      state.inCooloff = false;
      state.timer = null;
      if (state.queuedFn) {
        const queued = state.queuedFn;
        state.queuedFn = null;
        queued();
        this._startCooloff(channel);
      }
    }, this._cooloffMs);
  }

  // ============================================================================
  // Paramate Communication
  // ============================================================================
  private _validators = []
  private _validatorKeys = []
  public addValidator(fn:(value:any)=>boolean,key?:string){
    this._validators.push(fn)
    this._validatorKeys.push(key??null)
  }
  public removeValidator(id: number | string): boolean {
    let index = id as number;
    if (typeof id === 'string') {
      index = this._validatorKeys.findIndex((key) => key === id);
      if (index < 0) return false;
    }
    if (!Number.isInteger(index) || index < 0 || index >= this._validators.length) return false;

    // splice(index) with no delete count removed every validator from `index`
    // onward, and _validatorKeys was never spliced at all, so the two arrays
    // drifted apart and later key lookups pointed at the wrong validator.
    this._validators.splice(index, 1);
    this._validatorKeys.splice(index, 1);
    return true;
  }

  public clearValidators(){
    this._validators = []
    this._validatorKeys = []
  }

  protected _areValuesEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a.every((v, i) => v === b[i]);
    }
    return false;
  }

  protected _validateValue(value:any){
    const foundInvalid = (this._validators.map(vFn=>vFn(value)).includes(false))
    return !foundInvalid
  }
  protected _checkType(value:any):boolean{
    return true
  }

  /** Send value to Paramate - call after emitting change event. Debounced by _debounceMs. */
  protected _sendToParamate(value: any): void {
    if (!this._configurator || !this._paramName) return;

    // Mark as pending immediately for responsive UI - only in auto mode
    if (this._syncMode === 'auto') {
      if (this._areValuesEqual(value, this._lastConfirmedValue)) {
        clearParamPending(this._paramName);
      } else {
        markParamPending(this._paramName, value);
      }
    }

    if (this._debounceMs > 0) {
      if (this._debounceTimer !== null) {
        clearTimeout(this._debounceTimer);
      }
      this._debounceTimer = setTimeout(() => {
        this._debounceTimer = null;
        this._dispatchToParamate(value);
      }, this._debounceMs);
    } else {
      this._dispatchToParamate(value);
    }
  }

  /** Flush any pending debounced Paramate send immediately */
  public flushDebounce(): void {
    if (this._debounceTimer !== null) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = null;
    }
  }

  private _dispatchToParamate(value: any): void {
    if (!this._configurator || !this._paramName) return;

    const fullParamName = this._paramName.startsWith('main:')
      ? this._paramName
      : `main:${this._paramName}`;

    sendEv(this, 'as-input-pending', {
      detail: { paramName: this._paramName, value }
    });

    this._configurator.setParamValue?.(fullParamName, value, this._configureOnChange);
  }

  /** Takes the live configurator, then the param's value. withConfigurator()
   *  waits on the singleton slot, so it does not matter that an input is
   *  defined, and connects, before <as-configurator> is. */
  private _requestConfigurator(): void {
    if (!this._paramName) return;

    if (this._configurator) {
      this._syncInitialValue();
      return;
    }

    withConfigurator(this, (configurator) => {
      this._configurator = configurator;
      this._syncInitialValue();
    });
  }

  private _syncInitialValue(): void {
    if (!this._configurator?.params || !this._paramName) return;

    const paramData = lookupParam(this._configurator.params, this._paramName);
    if (paramData !== undefined) {
      this._isParamMissing = false;
      const groundTruthValue = paramData.value ?? paramData.default;
      const convertedValue = this._convertFromParamate(groundTruthValue);

      if (convertedValue !== undefined && convertedValue !== this._getValue()) {
        this._applyValue(convertedValue, {
          emitInput: false,
          emitChange: false,
          reflectAttr: true,
          commit: true,
          reason: 'paramate',
        });
        this._lastConfirmedValue = this._getValue();
      }

      // Apply min/max from param data if this input type supports them
      const observed = (this.constructor as any).observedAttributes as string[] | undefined;
      if (observed) {
        // Param-driven bounds go to the `auto-*` attributes so author-set `min`/`max`
        // are never clobbered and the bounds keep updating; the element combines the two
        // by taking the most restrictive.
        // See the "Paramate Integration" subtopic in appSrc/tutorial/src/topics/input-base.ts.
        if (paramData.min !== undefined && observed.includes('auto-min')) {
          this.setAttribute('auto-min', String(paramData.min));
        }
        if (paramData.max !== undefined && observed.includes('auto-max')) {
          this.setAttribute('auto-max', String(paramData.max));
        }
        if (paramData.type !== undefined && observed.includes('type') && !this.hasAttribute('type')) {
          this.setAttribute('type', String(paramData.type));
        }
        if (paramData.step !== undefined && observed.includes('step') && !this.hasAttribute('step')) {
          this.setAttribute('step', String(paramData.step));
        }
      }
    } else {
      this._isParamMissing = true;
      sendEv(this, 'as-input-param-missing', {
        detail: { paramName: this._paramName }
      });
      
      // Trigger visual update for warning icon
      if (typeof (this as any).updateDisplay === 'function') {
        (this as any).updateDisplay();
      } else if (typeof (this as any)._updateHelpHover === 'function') {
        (this as any)._updateHelpHover();
      }
    }
  }

  private _handleRestoreInputValues(event: Event): void {
    const { inputs, silent, reason } = (event as CustomEvent<{ inputs: Record<string, any>; silent?: boolean; reason?: string }>).detail || {};
    if (!inputs || typeof inputs !== 'object') return;
    const id = this.id;
    if (!id || !(id in inputs)) return;
    if ('setValue' in this) {
      // A project load without a session blob pushes a linked input's saved
      // value at Paramate through its change event. An undo or redo does not:
      // the history restores the params itself, and a change event here would
      // run the app's handler again and record a new step. That restore reports
      // on the sync channel, like every other value the user did not type.
      const fromHistory = reason === 'history';
      const isParam = this.hasAttribute('trcad-param');
      const silenceEvents = silent || fromHistory || !isParam;
      (this as any).setValue(inputs[id], { silenceEvents, reason: fromHistory ? 'history' : 'restore' });

      // Always dispatch native change event for focus-on-change support
      sendEv(this, 'change', {
        composed: false,
        detail: {} 
      });

      // Focus the inner element so the user can continue editing immediately
      setTimeout(() => {
        this._getInputElement()?.focus();
      }, 0);

      sendEv(this, 'as-input-restored', {
        detail: { id, value: inputs[id] }
      });
    }
  }

  private _handleRestoreParamValues(event: Event): void {
    if (!this._paramName) return;
    const { params } = (event as CustomEvent<{ params: Record<string, any> }>).detail || {};
    if (!params) return;

    const fullName = this._paramName.startsWith('main:') ? this._paramName : `main:${this._paramName}`;
    const value = fullName in params ? params[fullName]
      : this._paramName in params ? params[this._paramName]
      : undefined;
    if (value === undefined) return;

    const converted = this._convertFromParamate(value);
    this._applyValue(converted, { emitInput: false, emitChange: false, reflectAttr: true, commit: true, reason: 'history' });
    
    // Dispatch native change event so parent components (like as-step-selector) can react
    sendEv(this, 'change', {
      composed: false,
      detail: {}
    });

    // Focus the inner element
    setTimeout(() => {
      this._getInputElement()?.focus();
    }, 0);

    // Keep the UI input store in sync
    if (this.id) {
      internalData.uiInputs = { ...internalData.uiInputs, [this.id]: converted };
    }

    // When restoring for a configure action, we ARE pending until the configure finishes.
    // If we are restoring to ground truth (e.g. undoing a staged change), clear it.
    if (this._areValuesEqual(converted, this._lastConfirmedValue)) {
      clearParamPending(this._paramName);
    } else {
      markParamPending(this._paramName, converted);
    }
  }

  private _handleParamateValueResolved = (event: CustomEvent<{ paramName: string; value: any }>): void => {
    const { paramName, value } = event.detail;
    const previousValue = this._getValue();

    // If value is undefined (error case), revert to last confirmed value
    if (value === undefined) {
      if (this._lastConfirmedValue !== null && this._lastConfirmedValue !== previousValue) {
        this._applyValue(this._lastConfirmedValue, {
          emitInput: false,
          emitChange: false,
          reflectAttr: true,
          commit: true,
          reason: 'paramate',
        });
      }
      return;
    }

    // Update with ground truth value (silently to avoid loops)
    const convertedValue = this._convertFromParamate(value);
    if (convertedValue !== previousValue) {
      this._applyValue(convertedValue, {
        emitInput: false,
        emitChange: false,
        reflectAttr: true,
        commit: true,
        reason: 'paramate',
      });
    }

    // Store as last confirmed value
    this._lastConfirmedValue = this._getValue();

    sendEv(this, 'as-input-resolved', {
      detail: { paramName, value: this._lastConfirmedValue, previousValue }
    });
  }

  // ============================================================================
  // Event emission
  // ============================================================================

  /**
   * The one place a value event leaves an input. Subclasses pass the parts that
   * differ (`type`, the value, whatever extras they carry); `reason` and `phase`
   * are added here, along with the bubbles/composed flags every input shares.
   *
   * Which event fires:
   * - `'input'`   -> `as-input-update`, the in-progress phase (typing, dragging)
   * - `'change'`  -> `as-input-change`, a committed change that should drive app
   *                  logic — a user edit, or an explicit non-silenced setValue
   * - `'sync'`    -> `as-input-sync`, a committed change that should NOT drive
   *                  app logic — a project load, an undo, a param sync, a
   *                  binding update, a reset, or a silenced setValue
   *
   * Every committed change emits exactly one of change/sync, so nothing is
   * hidden from a listener that wants to see it, and nothing that only meant to
   * update the display can trigger work meant for real edits.
   */
  protected _emitValueEvent(
    phase: 'input' | 'change' | 'sync',
    reason: InputChangeReason,
    detail: Record<string, any>,
  ): void {
    const name = phase === 'input'
      ? 'as-input-update'
      : phase === 'change' ? 'as-input-change' : 'as-input-sync';

    sendEv(this, name, {
      detail: { ...detail, reason, phase }
    });
  }

  /** Pick the committed-change phase from whether the caller wanted it loud. */
  protected _commitPhase(emitChange: boolean | undefined): 'change' | 'sync' {
    return emitChange ? 'change' : 'sync';
  }

  private static _setterWarned = new Set<string>();

  /**
   * The `value` setter is quiet on purpose — it matches `input.value` on a
   * native element, and it's what display-sync code wants. That's a change from
   * the days when setting `.value` on a numbox or dropdown fired a full change,
   * and a quiet API going quieter is invisible, so say so once per tag in dev.
   */
  protected _warnOnValueSetter(): void {
    if (isProduction) return;
    const tag = this.tagName.toLowerCase();
    if (AsInputBase._setterWarned.has(tag)) return;
    AsInputBase._setterWarned.add(tag);
    console.warn(
      `[${tag}] Setting .value emits as-input-sync, not as-input-change, so it ` +
      `won't trigger listeners meant for real edits. Use setValue(v) for that, ` +
      `or setValue(v, { silenceEvents: true }) to stay quiet on purpose.`,
    );
  }

  // ============================================================================
  // Shared Utilities
  // ============================================================================

  /** Apply disabled state to input element */
  protected _applyDisabledState(): void {
    const input = this._getInputElement();
    if (input) {
      input.disabled = this.hasAttribute('disabled');
    }
  }

  // ============================================================================
  // Readonly & Locked State
  // ============================================================================

  get readonly(): boolean {
    return this.hasAttribute('readonly') || this.hasAttribute('locked');
  }

  set readonly(val: boolean) {
    if (val) {
      this.setAttribute('readonly', '');
    } else {
      this.removeAttribute('readonly');
    }
  }

  get locked(): boolean {
    return this.hasAttribute('locked');
  }

  set locked(val: boolean) {
    if (val) {
      this.setAttribute('locked', '');
    } else {
      this.removeAttribute('locked');
    }
  }

  get disabled(): boolean {
    return this.hasAttribute('disabled');
  }

  set disabled(val: boolean) {
    if (val) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }

  /** Apply readonly state. Subclasses can override for component-specific behavior. */
  protected _applyReadonlyState(): void {
    const input = this._getInputElement();
    if (input && 'readOnly' in input) {
      (input as HTMLInputElement).readOnly = this.readonly;
    }
  }
}
